/** Currency averages / per-unit rates — must be derived from period totals. */
const DERIVED_METRIC_KEYS = new Set([
  'average_order_value',
  'aov_incl_vat',
  'oac',
  'shipping_per_order',
  'operating_cost_per_order',
  'cac_total',
  'cogs_ratio',
  'cm1_margin_ratio',
  'shipping_share_of_sales',
  'roas_total',
  'roas_facebook',
  'roas_google',
  'marketing_spend_ratio',
]);

export type FinanceTotalRowLike = {
  metricKey: string;
  format: string;
  emphasis: string;
  values: Array<number | null | undefined>;
  compareValues?: Array<number | null | undefined>;
};

export function isFinanceMetricSummable(
  format: string,
  metricKey: string,
): boolean {
  if (format === 'percent' || format === 'ratio') return false;
  if (DERIVED_METRIC_KEYS.has(metricKey)) return false;
  if (metricKey.includes('_per_order')) return false;
  if (metricKey.endsWith('_ratio')) return false;
  if (metricKey.startsWith('roas_')) return false;
  if (metricKey.startsWith('aov')) return false;
  return format === 'currency' || format === 'count';
}

export function sumFinanceRowValues(
  values: Array<number | null | undefined>,
): number {
  return values.reduce<number>((acc, value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return acc;
    return acc + value;
  }, 0);
}

function safeRatio(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

function metricFromMap(
  totals: Record<string, number>,
  metricKey: string,
): number {
  return totals[metricKey] ?? 0;
}

/**
 * Recalculate ratio / average metrics from additive period totals
 * (same formulas as api enrichDerivedRow / applyFinanceCostPlan).
 */
export function deriveFinanceMetricFromTotals(
  metricKey: string,
  totals: Record<string, number>,
): number | null {
  const sales = metricFromMap(totals, 'total_sales');
  const orders = metricFromMap(totals, 'orders');
  const cogs = metricFromMap(totals, 'cost_of_products');
  const shipping = metricFromMap(totals, 'shipping');
  const netRevenue = metricFromMap(totals, 'net_revenue');
  const cm1 = metricFromMap(totals, 'cm1');
  const marketing = metricFromMap(totals, 'marketing_spend');
  const fb = metricFromMap(totals, 'facebook_marketing_spend');
  const google = metricFromMap(totals, 'google_ads_marketing_spend');

  switch (metricKey) {
    case 'aov_incl_vat':
    case 'average_order_value':
      return safeRatio(sales, orders);
    case 'oac':
    case 'cac_total':
      return safeRatio(marketing, orders);
    case 'cogs_ratio':
      return safeRatio(cogs, sales);
    case 'cm1_margin_ratio':
      return safeRatio(cm1, netRevenue);
    case 'shipping_per_order':
      return safeRatio(shipping, orders);
    case 'shipping_share_of_sales':
      return safeRatio(shipping, sales);
    case 'roas_total':
      return safeRatio(sales, marketing);
    case 'roas_facebook':
      return safeRatio(sales, fb);
    case 'roas_google':
      return safeRatio(sales, google);
    case 'marketing_spend_ratio':
      return safeRatio(marketing, sales);
    default:
      return null;
  }
}

/** Sum additive metrics across periods (skip section/spacer/derived). */
export function buildFinanceAdditiveTotals(
  rows: FinanceTotalRowLike[],
  field: 'values' | 'compareValues' = 'values',
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const row of rows) {
    if (row.emphasis === 'section' || row.emphasis === 'spacer') continue;
    if (!isFinanceMetricSummable(row.format, row.metricKey)) continue;
    const series = field === 'values' ? row.values : row.compareValues;
    if (!series?.length) continue;
    totals[row.metricKey] = sumFinanceRowValues(series);
  }
  return totals;
}

export function resolveFinanceRowTotal(
  row: FinanceTotalRowLike,
  additiveTotals: Record<string, number>,
): number | null {
  if (row.emphasis === 'section' || row.emphasis === 'spacer') return null;

  if (isFinanceMetricSummable(row.format, row.metricKey)) {
    return additiveTotals[row.metricKey] ?? sumFinanceRowValues(row.values);
  }

  return deriveFinanceMetricFromTotals(row.metricKey, additiveTotals);
}

/** Period-range total for a P&L row (sum or derived from additive totals). */
export function financeRowTotal(
  row: FinanceTotalRowLike,
  rows: FinanceTotalRowLike[],
): number | null {
  return resolveFinanceRowTotal(
    row,
    buildFinanceAdditiveTotals(rows, 'values'),
  );
}

export function financeRowCompareTotal(
  row: FinanceTotalRowLike,
  rows: FinanceTotalRowLike[],
): number | null {
  if (row.emphasis === 'section' || row.emphasis === 'spacer') return null;
  const hasCompare = rows.some((r) => (r.compareValues?.length ?? 0) > 0);
  if (!hasCompare) return null;
  return resolveFinanceRowTotal(
    row,
    buildFinanceAdditiveTotals(rows, 'compareValues'),
  );
}
